/**
 * BoardLayouts — list page for preset distribution-board frame layouts.
 *
 * Engineers later use saved layouts as a camera overlay before snapping
 * a photo of a real DB; this page is the authoring/management UI.
 *
 * Pattern mirrors PdfTemplates.jsx — list view + create dialog → click
 * a row to open the visual editor.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, LayoutPanelTop, Image as ImageIcon, Trash2, Copy } from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { PageHeader } from '../../components/Shared';
import {
  listBoardLayouts, createBoardLayout, deleteBoardLayout, duplicateBoardLayout,
} from '../../services/api';

const BoardLayouts = () => {
  const navigate = useNavigate();
  const [layouts, setLayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState({ name: '', description: '' });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listBoardLayouts();
      setLayouts(data?.layouts || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load layouts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!draft.name.trim()) {
      return toast.error('Give the layout a name');
    }
    setCreating(true);
    try {
      const created = await createBoardLayout({
        name: draft.name.trim(),
        description: draft.description.trim(),
        frames: [],
      });
      toast.success(`Created "${created.name}"`);
      setCreateOpen(false);
      setDraft({ name: '', description: '' });
      navigate(`/admin/board-layouts/${created.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create layout');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (layout) => {
    if (!window.confirm(`Delete layout "${layout.name}"? This cannot be undone.`)) return;
    try {
      await deleteBoardLayout(layout.id);
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete');
    }
  };

  const handleDuplicate = async (layout) => {
    try {
      const copy = await duplicateBoardLayout(layout.id);
      toast.success(`Duplicated as "${copy.name}"`);
      navigate(`/admin/board-layouts/${copy.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not duplicate');
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="admin-board-layouts">
      <PageHeader
        title="Board Layouts"
        description="Pre-design distribution-board templates with breaker / isolator / circuit-label frames. Engineers will line a saved layout up with the real board before taking a cert photo."
        actions={
          <Button onClick={() => setCreateOpen(true)} data-testid="new-board-layout-btn">
            <Plus className="h-4 w-4 mr-1" /> New layout
          </Button>
        }
      />

      {loading ? (
        <div className="text-sm text-slate-500">Loading...</div>
      ) : layouts.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-md p-12 text-center">
          <LayoutPanelTop className="h-10 w-10 mx-auto text-slate-400" />
          <h3 className="mt-3 text-base font-medium text-slate-700">No layouts yet</h3>
          <p className="mt-1 text-sm text-slate-500 max-w-sm mx-auto">
            Create your first layout — name it after a customer site or a typical board you encounter, then
            drop frames over a reference photo.
          </p>
          <Button className="mt-5" onClick={() => setCreateOpen(true)} data-testid="empty-new-board-layout-btn">
            <Plus className="h-4 w-4 mr-1" /> New layout
          </Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {layouts.map((l) => (
            <div
              key={l.id}
              className="group relative bg-white border border-slate-200 rounded-md p-5 hover:border-blue-400 hover:shadow-sm cursor-pointer transition-all"
              onClick={() => navigate(`/admin/board-layouts/${l.id}`)}
              data-testid={`board-layout-card-${l.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <h3 className="font-medium text-slate-900 truncate">{l.name}</h3>
                  {l.description && (
                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{l.description}</p>
                  )}
                </div>
                <div className="flex items-start gap-1 flex-shrink-0">
                  <button
                    className="p-1 text-slate-400 hover:text-blue-700 sm:opacity-60 sm:group-hover:opacity-100 transition-opacity"
                    onClick={(e) => { e.stopPropagation(); handleDuplicate(l); }}
                    data-testid={`duplicate-board-layout-${l.id}`}
                    title="Duplicate layout"
                    aria-label="Duplicate layout"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                  <button
                    className="p-1 text-slate-400 hover:text-red-600 sm:opacity-60 sm:group-hover:opacity-100 transition-opacity"
                    onClick={(e) => { e.stopPropagation(); handleDelete(l); }}
                    data-testid={`delete-board-layout-${l.id}`}
                    title="Delete layout"
                    aria-label="Delete layout"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-3 text-xs text-slate-500">
                <span className="inline-flex items-center gap-1">
                  <LayoutPanelTop className="h-3.5 w-3.5" />
                  {l.frames?.length || 0} frame{l.frames?.length === 1 ? '' : 's'}
                </span>
                {l.hasPhoto && (
                  <span className="inline-flex items-center gap-1 text-emerald-700">
                    <ImageIcon className="h-3.5 w-3.5" /> Reference photo
                  </span>
                )}
              </div>
              {l.updatedAt && (
                <div className="mt-2 text-[11px] text-slate-400">
                  Updated {l.updatedAt.slice(0, 10)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New board layout</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input
                className="mt-1.5"
                placeholder="e.g. SCERRIES — DB1 (3-phase, 18-way)"
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                data-testid="new-board-layout-name-input"
              />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Textarea
                rows={2}
                className="mt-1.5"
                placeholder="What kind of board this layout is for"
                value={draft.description}
                onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button
              onClick={handleCreate}
              disabled={creating || !draft.name.trim()}
              data-testid="confirm-new-board-layout-btn"
            >
              {creating ? 'Creating...' : 'Create & open editor'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BoardLayouts;
